/**
 * Krok TAB1 bod 9 — ŘÁDEK „CO ZAKÁZKU BRZDÍ“ NA KARTĚ KANBANU.
 *
 * Předloha `docs/design/ux-v3/tabule/ZakazkyKanban.png` má pod adresou jeden
 * řádek („Čeká na list vlastnictví“, „Smlouva čeká na podpis“, „Předání klíčů
 * dnes 17:00“) a poznámka k návrhu u něj říká: *„Řádek pod adresou = co teď
 * zakázku brzdí (stejná funkce jako Dnešek a detail zakázky).“*
 *
 * ---------------------------------------------------------------------------
 * ROZHODNUTÍ TAB1-BRZDI — co tedy je „stejná funkce“
 *
 * V repu dnes ŽÁDNÁ funkce „co brzdí“ neexistuje a tři obrazovky mluví každá
 * o něčem jiném:
 *
 *  | obrazovka            | co ukazuje                   | odkud                       |
 *  |----------------------|------------------------------|-----------------------------|
 *  | Dnešek, „v pohybu“   | co se NAPOSLEDY STALO        | `lib/case-last-event.ts`    |
 *  | detail zakázky       | DALŠÍ KROK jako celá karta   | `lib/case-advice.ts`        |
 *  | `/zakazky` tabulka   | DALŠÍ KROK jedním řádkem     | `lib/case-next-step.ts`     |
 *  | kanban (do TAB1)     | `item.nextTaskTitle`         | server, tabulka `case_tasks`|
 *
 * `caseAdvice` (detail) stojí na `CaseDetail` — celém auditním případu se
 * všemi otázkami a fakty. Seznam zakázek ho pro 100 karet načíst nemůže, a
 * vymýšlet pro kartu NOVÝ text zadání výslovně zakazuje. `caseEventLines`
 * („Poslední dění" v detailu) odpovídá na jinou otázku — minulost, ne
 * překážku.
 *
 * Zbývá tedy ta jediná věta o budoucnosti, kterou seznam zakázek SKUTEČNĚ má:
 * **nejbližší otevřený úkol zakázky**. Je to přesně ta informace, ze které
 * `caseAdvice` dělá bod 3 své kaskády („něco jiného je otevřené → první
 * otevřený úkol“), a přesně to, co tabulka `/zakazky` tiskne ve sloupci
 * „Další krok“. Tenhle modul to dává na JEDNO místo, aby tabulka a kanban
 * nemohly u téže zakázky říct dvě různé věci — do TAB1 tabulka četla
 * `nextTaskTitle ?? nextStepByCase`, karta jen `nextTaskTitle` (který je na
 * demo datech vždy `null`, viz `lib/case-next-step.ts`), takže karta řádek
 * NEMĚLA VŮBEC.
 *
 * Co se tím NESLIBUJE: tohle není analýza rizik. Když zakázka otevřený úkol
 * nemá, funkce vrátí `null` a obrazovka řádek nevykreslí — nic se nedomýšlí.
 * ---------------------------------------------------------------------------
 */

export interface CaseBlockerInput {
  /** Serverová hodnota `CaseDto.nextTaskTitle` (tabulka `case_tasks`). */
  readonly nextTaskTitle: string | null | undefined;
  /** Nejbližší otevřený úkol makléře z `crm_tasks` (`nextStepByCase`). */
  readonly crmNextStep: string | null | undefined;
}

/**
 * Jedna věta o tom, co zakázku drží, nebo `null`.
 *
 * Pořadí zdrojů je TOTÉŽ jako v tabulce `/zakazky` od kroku FIX6 bod 7:
 * serverová hodnota má přednost (počítá ji stavový automat případu), úkol
 * makléře je záloha pro zakázky, kde `case_tasks` nikdo nepíše.
 */
export function caseBlockerLine(input: CaseBlockerInput): string | null {
  const fromServer = normalize(input.nextTaskTitle);
  if (fromServer !== null) return fromServer;
  return normalize(input.crmNextStep);
}

function normalize(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}
